import { createAsyncThunk, isRejectedWithValue } from '@reduxjs/toolkit';
import { apiInstance } from '../../api/apiInstance';
import { RootState } from '../../store/store';
import { User } from './constants';
import { companyInformationInterface } from '../../utils/constant';
import { UserProfileInfoDto } from '../company/constants';

export const generateVerificationToken = createAsyncThunk(
  'user/generateVerificationToken',
  async (invToken: string | undefined, { getState, rejectWithValue }) => {
    const state = getState() as { user: User };
    const formData = state.user.signUpForm;
    try {
      await apiInstance.post('/auth/sign-up', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        inviteToken: invToken !== undefined ? invToken : undefined,
      });

      return { hasInviteToken: invToken !== undefined ? true : false };
    } catch (err: any) {
      return rejectWithValue(err.response.data.message);
    }
  },
);

export const verifyEmail = createAsyncThunk(
  'user/verifyEmail',
  async (origin: 'signin' | 'signup' | 'reset-email', { getState, rejectWithValue }) => {
    const state = getState() as { user: User };
    const emailToUse =
      origin === 'signin'
        ? state.user.signInForm.email
        : origin === 'signup'
          ? state.user.signUpForm.email
          : state.user.resetEmailForm.email;
    try {
      const response = await apiInstance.post('/auth/verify', {
        code: state.user.verificationForm.code,
        email: emailToUse,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data.message);
    }
  },
);

export const verifyEmailViaLink = createAsyncThunk(
  'user/verifyEmailViaLink',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post('/auth/verify-link', { token });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Verification failed');
    }
  },
);

export const matchOrg = createAsyncThunk('user/matchOrganization', async (_, { getState, rejectWithValue }) => {
  const state = getState() as { user: User };
  try {
    const tokenString = localStorage.getItem('signupToken');
    const signupToken = state.user.signupToken ? state.user.signupToken : tokenString;

    const response = await apiInstance.get('/auth/match-organization', {
      headers: {
        Authorization: `Bearer ${signupToken}`,
      },
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data.message);
  }
});

export const createAccount = createAsyncThunk('user/createAccount', async (_, { getState, rejectWithValue }) => {
  const state = getState() as RootState;
  try {
    const tokenString = localStorage.getItem('signupToken');
    const signupToken = state.user.signupToken ? state.user.signupToken : tokenString;

    const response = await apiInstance.post(
      '/auth/default-settings',
      {
        email: state.user.signUpForm.email,
        defaultSettings: {
          returnAddress: state.settings.returnAddress.formData,
          signature: state.settings.signature.formData,
          courtLocation: state.settings.courtLocation.formData,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${signupToken}`,
        },
      },
    );
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data.message);
  }
});

export const authenticateUser = createAsyncThunk(
  'user/authenticateUser',
  async ({ formType }: { formType: 'signInForm' | 'signUpForm' }, { getState, rejectWithValue }) => {
    const state = getState() as { user: User };
    const form = state.user[formType];

    try {
      const response = await apiInstance.post('/auth/sign-in', {
        email: form.email,
        password: form.password,
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const askPasswordReset = createAsyncThunk('user/askPasswordReset', async (_, { getState, rejectWithValue }) => {
  const state = getState() as { user: User };
  const email = state.user.passwordResetForm.email || state.user.currentUser.email;
  try {
    const response = await apiInstance.post('/auth/ask-password-reset', {
      email,
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data.message);
  }
});

// Use this thunk for the unauthenticated /auth/reset flow.
// Unlike askPasswordReset, it only reads from the form field and never
// falls back to the logged-in user's email stored in currentUser.
export const askPasswordResetFromForm = createAsyncThunk(
  'user/askPasswordResetFromForm',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { user: User };
    const email = state.user.passwordResetForm.email;
    try {
      const response = await apiInstance.post('/auth/ask-password-reset', {
        email,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data.message);
    }
  },
);

export const resetPassword = createAsyncThunk('user/resetPassword', async (_, { getState, rejectWithValue }) => {
  const state = getState() as { user: User };
  const formData = state.user.passwordResetForm;
  try {
    // Link flow: send token + new password.
    // Code flow: send email + code + new password.
    const payload = formData.token
      ? { password: formData.newPassword, token: formData.token }
      : { password: formData.newPassword, email: formData.email, code: formData.code };
    const response = await apiInstance.post('/auth/reset-password', payload);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data.message);
  }
});

export const logoutUser = createAsyncThunk('user/logout', async (_, { rejectWithValue }) => {
  try {
    await apiInstance.post('/auth/logout');
  } catch (err: any) {
    // Logout should not block UI — swallow errors
  }
});

export const resendVerificationCode = createAsyncThunk(
  'user/resendVerificationCode',
  async (origin: 'signin' | 'signup' | 'reset-email', { getState, rejectWithValue }) => {
    const state = getState() as { user: User };
    const emailToUse =
      origin === 'signin'
        ? state.user.signInForm.email
        : origin === 'signup'
          ? state.user.signUpForm.email
          : state.user.resetEmailForm.email;
    try {
      const response = await apiInstance.post('/auth/verification-code', {
        email: emailToUse,
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data.message);
    }
  },
);

export const refreshToken = createAsyncThunk('user/refreshToken', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.post('/auth/refresh');
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data.message);
  }
});

export const createOrganization = createAsyncThunk(
  'user/createOrganization',
  async (companyInformation: companyInformationInterface, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const tokenString = localStorage.getItem('signupToken');

      const signupToken = state.user.signupToken ? state.user.signupToken : tokenString;

      const organizationMapper = {
        name: companyInformation.companyName,
        type:
          companyInformation.organizationType === 'Other' && companyInformation.customOrgType
            ? companyInformation.customOrgType
            : companyInformation.organizationType,
        countryCode: companyInformation.countryCode,
        companyPhoneNumber: companyInformation.companyPhoneNumber.replace(/\D/g, ''),
        addressOne: companyInformation.addressOne,
        addressTwo: companyInformation.addressTwo,
        city: companyInformation.city,
        state: companyInformation.state,
        zipCode: companyInformation.zip,
        ...(companyInformation.surveyAnswer && {
          discoverySource: companyInformation.surveyAnswer,
        }),
        ...(companyInformation.surveyAnswerDetail && {
          discoverySourceDetail: companyInformation.surveyAnswerDetail,
        }),
      };

      const response = await apiInstance.post(
        '/auth/establish-organization',
        {
          ...organizationMapper,
        },
        {
          headers: {
            Authorization: `Bearer ${signupToken}`,
          },
        },
      );
    } catch (error: any) {
      return rejectWithValue({
        message: error.response.data.message,
        errorStack: error.response.data.errorStack || [],
      });
    }
  },
);

export const joinOrganization = createAsyncThunk('user/joinOrganization', async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState() as RootState;

    const tokenString = localStorage.getItem('signupToken');

    const signupToken = state.user.signupToken ? state.user.signupToken : tokenString;

    const response = await apiInstance.post(
      '/auth/join-organization',
      {},
      {
        headers: {
          Authorization: `Bearer ${signupToken}`,
        },
      },
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const resetEmail = createAsyncThunk('user/resetEmail', async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState() as RootState;

    const tokenString = localStorage.getItem('signupToken');

    const signupToken = state.user.signupToken ? state.user.signupToken : tokenString;

    const response = await apiInstance.post(
      '/auth/reset-registration-email',
      {
        newEmail: state.user.resetEmailForm.email,
      },
      {
        headers: {
          Authorization: `Bearer ${signupToken}`,
        },
      },
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const checkRequestJoinOrgStatus = createAsyncThunk(
  'user/check-status',
  async (accessToken: string, { rejectWithValue, getState }) => {
    try {
      const response = await apiInstance.get('/auth/check-status', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data;
    } catch (error) { }
  },
);

export const editUserProfile = createAsyncThunk(
  'user/edit-profile',
  async (newProfileInfo: UserProfileInfoDto, { rejectWithValue }) => {
    try {
      const formattedInfo = Object.fromEntries(
        Object.entries(newProfileInfo).map(([k, v]) => [k, v === '' ? null : v]),
      );

      const response = await apiInstance.patch('/profile', formattedInfo);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const refreshPassword = createAsyncThunk(
  'user/refresh-password',
  async (newPasswordInfo: { oldPassword: string; password: string; confirmPassword: string }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post('/auth/refresh-password', {
        ...newPasswordInfo,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const askEmailChange = createAsyncThunk(
  'user/ask-email-change',
  async (newEmailInfo: { password: string; newEmail: string }, { rejectWithValue }) => {
    let isPasswordConfirmed = false;
    try {
      const response = await apiInstance.post('/auth/confirm-password', {
        password: newEmailInfo.password,
      });

      if (response.status === 200) {
        isPasswordConfirmed = true;
      }
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }

    if (isPasswordConfirmed) {
      try {
        const response = await apiInstance.post('/auth/ask-email-refresh', {
          newEmail: newEmailInfo.newEmail,
        });

        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data.message);
      }
    }
  },
);

export const confirmEmailChange = createAsyncThunk(
  'user/confirm-email-change',
  async (resetToken: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiInstance.post(
        '/auth/refresh-email',
        {
          resetToken,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getDashboardMetrics = createAsyncThunk('/user/get-dashboard-metrics', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/job/metrics');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getActiveBanner = createAsyncThunk('admin/getActiveBanner', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/banner/active');

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});
